import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import type { Product } from "@/data/products";
import { useCart } from "@/hooks/use-cart";
import { ShoppingCart } from "lucide-react";

function BadgeLabel({ badge }: { badge: Product["badge"] }) {
  if (!badge) return null;
  const styles = {
    bestseller: "bg-badge-success text-electric-foreground shadow-[0_4px_12px_-2px_hsl(var(--badge-success)/0.5)]",
    new: "bg-badge-new text-electric-foreground shadow-[0_4px_12px_-2px_hsl(var(--badge-new)/0.5)]",
    bundle: "bg-electric text-electric-foreground shadow-electric",
  };
  const labels = { bestseller: "★ Bestseller", new: "Nouveauté", bundle: "Pack" };
  return (
    <motion.span
      initial={{ opacity: 0, y: -8, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide z-10 backdrop-blur-sm ${styles[badge]}`}
    >
      {labels[badge]}
    </motion.span>
  );
}

export function ProductCard({ product, index = 0 }: { product: Product; index?: number }) {
  const { addItem } = useCart();

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.6,
        delay: Math.min(index * 0.06, 0.4),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group"
    >
      <Link to="/product/$slug" params={{ slug: product.slug }} className="block">
        <div className="relative bg-card rounded-2xl overflow-hidden shadow-card transition-all duration-500 ease-out will-change-transform group-hover:shadow-card-hover group-hover:-translate-y-1.5 ring-1 ring-border/40 group-hover:ring-electric/30">
          <div className="relative aspect-[4/3] bg-muted overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              width={800}
              height={600}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
            />
            {/* Subtle gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <BadgeLabel badge={product.badge} />
          </div>
          <div className="p-5">
            <h3 className="font-display font-semibold text-foreground text-sm leading-snug mb-2 group-hover:text-electric transition-colors">
              {product.name}
            </h3>
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{product.shortDescription}</p>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[10px] font-bold text-electric bg-electric/10 px-1.5 py-0.5 rounded-full animate-pulse">✨ Aujourd'hui seulement</span>
              <span className="text-[10px] text-muted-foreground">-10%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-foreground">{product.price.toFixed(2)}€</span>
                {product.originalPrice && (
                  <span className="text-sm text-muted-foreground line-through">{product.originalPrice.toFixed(2)}€</span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addItem(product);
                }}
                aria-label={`Ajouter ${product.name} au panier`}
                className="p-2 rounded-full bg-electric text-electric-foreground transition-all duration-300 ease-out shadow-electric hover:scale-110 hover:shadow-[0_8px_20px_-4px_hsl(var(--electric)/0.6)] active:scale-95"
              >
                <ShoppingCart className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
